import {useCallback} from "react";

import {AABB, type TLevelObject} from "@repo/shared";
import {Vector2} from "three";

import useMyCamera from "~/contexts/camera/useMyCamera";
import {useGameState} from "~/contexts/gamestate/useGameState";

import {useLevelLoaderContext} from "./LevelLoaderContext";

const useLevelLoader = () => {
  const {clear, add} = useGameState();
  const {setCurrentPosition, setCurrentZoom, targetPosition, targetZoom} =
    useMyCamera();

  // Get the global last loaded level id and its updater
  const {lastLevelId, setLastLevelId} = useLevelLoaderContext();

  return useCallback(
    (
      levelId: string,
      level: {data: TLevelObject[]},
      force: boolean = false,
    ) => {
      console.log("RELOADING LEVEL!!!!");

      if (!force && lastLevelId === levelId) {
        return;
      }

      // Update the global last loaded level id.
      setLastLevelId(levelId);

      // Update the game state with the new level data.
      clear();
      add(level.data);

      if (force) return;

      // Calculate the bounding box values.
      const boundingBox = AABB.fromTLevelObjects(level.data);
      const width = boundingBox.upperBound.x - boundingBox.lowerBound.x;
      const height = boundingBox.upperBound.y - boundingBox.lowerBound.y;

      // Calculate zoom factor based on window dimensions and bounding box sizes.
      const zoom =
        Math.min(window.innerWidth / width, window.innerHeight / height) * 0.9;

      // Set the camera position to the center of the bounding box.
      const position = new Vector2(
        boundingBox.getCenter().x,
        boundingBox.getCenter().y,
      );
      // Use the new setters to update the current camera state.
      // These setters also reset the smooth velocity.
      setCurrentPosition(position);
      targetPosition.current = position;
      setCurrentZoom(zoom / 1.5);
      targetZoom.current = zoom;
    },
    [
      lastLevelId,
      setLastLevelId,
      clear,
      add,
      setCurrentPosition,
      targetPosition,
      setCurrentZoom,
      targetZoom,
    ],
  );
};

export default useLevelLoader;
